import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authService } from "../services/authService";
import { TENANT_SEGMENT_STORAGE_KEY } from "./session";

const AuthContext = createContext(null);

/**
 * Persiste `profile.tenant.segment` em localStorage para que páginas como
 * `Agenda.js` consigam filtrar o menu lateral por segmento ANTES do
 * `businessData` (request /tenants/me) chegar — evita flicker do item
 * "Fidelidade" para tenants não-barbearia no primeiro render.
 *
 * try/catch defensivo: localStorage pode estar indisponível em modo
 * privado de browsers antigos ou com quota cheia. Falha silenciosa é
 * preferível a quebrar o login.
 */
function persistTenantSegment(profile) {
  const segment = profile?.tenant?.segment;
  try {
    if (typeof segment === "string" && segment.length > 0) {
      window.localStorage.setItem(TENANT_SEGMENT_STORAGE_KEY, segment);
    } else {
      // Sem segment no profile → remove cache stale (ex.: tenant trocou).
      // Não é caminho de logout: é invalidação de cache no login/refresh,
      // pareada com o setItem acima. INV-F2 não se aplica aqui — encerrar
      // sessão continua sendo exclusividade de `clearSession()`.
      window.localStorage.removeItem(TENANT_SEGMENT_STORAGE_KEY);
    }
  } catch {
    // localStorage indisponível — ignorar.
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  /**
   * `true` enquanto o bootstrap (localStorage + primeiro `/auth/me`) não
   * resolveu. Existe para que a guarda de onboarding consiga distinguir
   * "ainda não sei quem é o usuário" de "o usuário não tem tenant" — sem esse
   * bit, `user === null` no primeiro render seria lido como "sem tenant" e todo
   * mundo levaria um redirect em falso para `/onboarding`.
   *
   * Espelha o formato de `useTenantRole()`, que também começa `loading: true`.
   * Quem consome faz `if (loading) return null`, como o `RoleRoute` já faz.
   */
  const [loading, setLoading] = useState(true);
  const isAuthenticated = !!user;

  useEffect(() => {
    const saved = window.localStorage.getItem("user");
    if (!saved) {
      // Nada em storage: não há o que hidratar, o bootstrap acabou aqui.
      setLoading(false);
      return;
    }

    let parsed = null;
    try {
      parsed = JSON.parse(saved);
    } catch {
      // localStorage corrompido — ignorar.
    }

    if (!parsed || !parsed.email) {
      setLoading(false);
      return;
    }

    setUser(parsed);
    persistTenantSegment(parsed);

    // Refresh assíncrono para buscar dados frescos (incluindo tenant.plan).
    //
    // ⚠️ `authService.getProfile()` ENGOLE a falha do `/auth/me`: devolve `null`
    // em vez de rejeitar. Por isso o `setLoading(false)` mora no `finally` e não
    // no `then` — se dependesse do sucesso, um `/auth/me` fora do ar deixaria
    // `loading` preso em `true` para sempre, e a guarda (`if (loading) return
    // null`) pintaria a aplicação inteira de branco. Falha de refresh degrada
    // para o perfil de storage; não pode travar a navegação.
    authService
      .getProfile()
      .then((profile) => {
        if (profile) {
          setUser(profile);
          persistTenantSegment(profile);
        }
      })
      .catch(() => {}) // silencioso se token expirou
      .finally(() => setLoading(false));
  }, []);

  /**
   * Rebusca `/auth/me` e devolve o perfil resultante.
   *
   * DEVOLVE o perfil (em vez de só empurrá-lo para o estado) porque quem chama
   * logo depois do login — `AuthSuccess` — precisa decidir o destino da
   * navegação no MESMO tick. Ler `user` do contexto ali daria o valor anterior:
   * o `setUser` só aparece no próximo render, e o `navigate` já teria acontecido
   * com base num usuário desatualizado.
   *
   * @returns {Promise<object|null>} O perfil fresco; na falha, o perfil de
   *   storage (degradação); `null` se não houver nem um nem outro.
   */
  const refreshUser = useCallback(async () => {
    try {
      const profile = await authService.getProfile();
      if (profile) {
        setUser(profile);
        persistTenantSegment(profile);
        return profile;
      }
    } catch {
      // cai no fallback de storage abaixo
    }

    const saved = window.localStorage.getItem("user");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed) {
          setUser(parsed);
          persistTenantSegment(parsed);
          return parsed;
        }
      } catch {
        // localStorage corrompido — ignorar
      }
    }
    return null;
  }, []);

  // INV-F2: `authService.logout()` delega a `clearSession()`, que já inclui o
  // cache de segmento do tenant. O antigo `clearTenantSegment()` explícito
  // aqui virou redundante e foi removido — duas chamadas dariam a impressão
  // de que existem dois conjuntos de chaves, que é exatamente a divergência
  // que o invariante elimina. Sobra ao contexto o que só ele sabe fazer:
  // zerar o estado React.
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  const register = useCallback(async (data) => {
    const profile = {
      name: data?.nome || "Usuário",
      email: data?.email,
      empresaSegmento: data?.segmento,
      desafios: data?.desafios || [],
      expectativa: data?.expectativa || [],
      qtdClientes: data?.qtdClientes || 0,
    };
    if (!profile.email) {
      throw new Error("E-mail é obrigatório no cadastro");
    }

    setUser(profile);
    return true;
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      loading,
      logout,
      register,
      refreshUser,
    }),
    [isAuthenticated, user, loading, logout, register, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
}
